import { useEffect, useMemo, useState, useCallback } from 'react';
import './NewsUpdatesPage.css';
import { getImageUrl } from './utils/mediaUtils';
import { getAPIBase } from './config';
import { X, Calendar, Megaphone, ExternalLink, User, ArrowRight, BookOpen } from 'lucide-react';

const API_BASE = (() => {
  if (process.env.REACT_APP_API_BASE) return process.env.REACT_APP_API_BASE;
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const { hostname, protocol } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://permataharapansecondary.sch.id/api';
    }
    return `${protocol}//${hostname}/api`;
  }
  return 'https://permataharapansecondary.sch.id/api';
})();

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg') || lower.includes('youtube.com') || lower.includes('youtu.be');
};

const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return m ? m[1] : null;
};

function NewsUpdatesPage({ page, onNavigate }) {
  // 1. Media Map from CMS
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_homepage') || sessionStorage.getItem('cms_media_map');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  // 2. Published sections map directly from content_published
  const [liveSections, setLiveSections] = useState(() => {
    if (page?.sections && Object.keys(page.sections).length > 0) {
      return page.sections;
    }
    try {
      const cached = sessionStorage.getItem('cms_page_news-updates');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });

  const [loading, setLoading] = useState(!liveSections);

  // Fetch CMS media
  useEffect(() => {
    let isMounted = true;
    const loadMedia = async () => {
      try {
        const apiBase = getAPIBase ? getAPIBase() : API_BASE;
        const res = await fetch(`${apiBase}/homepage/cms/media`, { credentials: 'omit' });
        if (res.ok) {
          const json = await res.json();
          const items = Array.isArray(json.data) ? json.data : [];
          const map = {};
          items.forEach((m) => {
            const mId = m.id || m.media_id;
            const mUrl = m.url || m.file_path;
            if (mId && mUrl) {
              map[mId] = mUrl;
              map[String(mId)] = mUrl;
            }
            if (m.filename && mUrl) map[m.filename] = mUrl;
            if (m.original_filename && mUrl) map[m.original_filename] = mUrl;
          });
          if (isMounted && Object.keys(map).length > 0) {
            setMediaMap(map);
            try {
              sessionStorage.setItem('cms_media_homepage', JSON.stringify(map));
            } catch (_) {}
          }
        }
      } catch (e) {
        console.warn('[NewsUpdatesPage] Media fetch error:', e);
      }
    };
    loadMedia();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch published sections directly from database (content_published)
  const fetchPublishedSections = useCallback(async () => {
    try {
      const apiBase = getAPIBase ? getAPIBase() : API_BASE;
      const res = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=news-updates&_t=${Date.now()}`, {
        credentials: 'omit',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });
      if (res.ok) {
        const json = await res.json();
        const sections = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        if (sections.length > 0) {
          const sectionMap = {};
          sections.forEach((s) => {
            const cp = s.content_published && s.content_published !== '{}' && s.content_published !== '[]'
              ? s.content_published
              : (s.content || s.content_published);
            let parsed = null;
            try {
              parsed = typeof cp === 'string' ? JSON.parse(cp) : cp;
            } catch (e) {
              parsed = cp;
            }
            if (parsed) {
              sectionMap[s.section_type] = parsed.en || parsed;
            }
          });
          if (Object.keys(sectionMap).length > 0) {
            setLiveSections(sectionMap);
            try {
              sessionStorage.setItem('cms_page_news-updates', JSON.stringify(sectionMap));
            } catch (_) {}
          }
        }
      }
    } catch (err) {
      console.warn('[NewsUpdatesPage] Error fetching published news sections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPublishedSections();

    const handleUpdate = () => {
      fetchPublishedSections();
    };
    window.addEventListener('approvalCompleted', handleUpdate);
    window.addEventListener('cmsContentUpdated', handleUpdate);

    return () => {
      window.removeEventListener('approvalCompleted', handleUpdate);
      window.removeEventListener('cmsContentUpdated', handleUpdate);
    };
  }, [fetchPublishedSections]);

  // Sync if page prop updates
  useEffect(() => {
    if (page?.sections && Object.keys(page.sections).length > 0) {
      setLiveSections((prev) => (prev ? { ...page.sections, ...prev } : page.sections));
    }
  }, [page?.sections]);

  // Pure dynamic media resolver - strictly zero hardcoded image fallbacks
  const resolveMedia = useCallback(
    (src) => {
      if (!src) return '';
      if (typeof src === 'object' && src !== null) {
        src = src.image || src.url || src.file_path || src.src || '';
      }
      const str = String(src).trim();
      if (!str) return '';
      if (
        str.startsWith('http://') ||
        str.startsWith('https://') ||
        str.startsWith('blob:') ||
        str.startsWith('data:')
      ) {
        return str;
      }
      if (mediaMap && (mediaMap[str] || mediaMap[String(str)])) {
        return mediaMap[str] || mediaMap[String(str)];
      }
      const helperUrl = getImageUrl(str, mediaMap);
      if (helperUrl && helperUrl !== str && helperUrl !== '') {
        return helperUrl;
      }
      if (str.startsWith('/')) {
        return `${API_ORIGIN}${str}`;
      }
      return `${API_ORIGIN}/${str}`;
    },
    [mediaMap]
  );

  // 1. HERO SECTION DATA
  const heroSec = liveSections?.['news-hero'] || page?.sections?.['news-hero'] || {};
  const heroKicker = heroSec?.kicker || page?.kicker || '';
  const heroTitle = heroSec?.title || page?.title || '';
  const heroSummary = heroSec?.summary || heroSec?.subtitle || page?.summary || page?.subtitle || '';

  const heroSlides = useMemo(() => {
    const rawSlides = heroSec?.slides || heroSec?.hero_slides || page?.hero_slides || page?.slides || [];
    if (!Array.isArray(rawSlides)) return [];

    return rawSlides
      .map((slide, idx) => {
        const rawUrl = typeof slide === 'object' && slide !== null
          ? (slide.image || slide.url || slide.file_path || slide.src)
          : slide;
        const url = resolveMedia(rawUrl);
        const isVideo = isVideoUrl(url);
        const youtubeId = isVideo ? extractYouTubeId(url) : null;
        return {
          id: slide?.id || idx,
          url,
          caption: slide?.caption || '',
          isVideo,
          isYouTube: Boolean(youtubeId),
          youtubeId,
        };
      })
      .filter((s) => Boolean(s.url));
  }, [heroSec?.slides, heroSec?.hero_slides, page?.hero_slides, page?.slides, resolveMedia]);

  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [activePressModal, setActivePressModal] = useState(null);

  useEffect(() => {
    if (!heroSlides.length) return;
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  // 2. PRESS RELEASES DATA
  const pressSec = liveSections?.['news-press'] || page?.sections?.['news-press'] || {};
  const pressKicker = pressSec?.kicker || page?.press_releases_kicker || '';
  const pressTitle = pressSec?.title || page?.press_releases_title || '';
  const pressDescription = pressSec?.description || page?.press_releases_description || '';

  const pressReleases = useMemo(() => {
    const rawList = pressSec?.items || pressSec?.press_releases || page?.press_releases || [];
    if (!Array.isArray(rawList)) return [];

    return rawList.map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `press-${index + 1}`,
          title: item,
          subtitle: '',
          category: 'Press Release',
          author: '',
          description: '',
          content: '',
          image: '',
          images: [],
          highlights: [],
          features: [],
          date: '',
          link: `news-press-${index + 1}`,
        };
      }

      const title = item?.title || '';
      const normalizedId = String(item?.id || title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const resolvedImage = resolveMedia(item?.image);
      let itemImages = [];
      if (Array.isArray(item?.images) && item.images.length > 0) {
        itemImages = item.images.map((img) => resolveMedia(img)).filter(Boolean);
      } else if (resolvedImage) {
        itemImages = [resolvedImage];
      }

      return {
        id: normalizedId || `press-${index + 1}`,
        title,
        subtitle: item?.subtitle || '',
        category: item?.category || item?.tag || 'Press Release',
        author: item?.author || '',
        description: item?.description || '',
        content: item?.content || item?.body || item?.description || '',
        image: resolvedImage,
        images: itemImages,
        highlights: Array.isArray(item?.highlights)
          ? item.highlights
          : typeof item?.highlights === 'string'
          ? [item.highlights]
          : [],
        features: Array.isArray(item?.features) ? item.features : [],
        date: item?.date || '',
        link: item?.link || (normalizedId ? `news-press-${normalizedId}` : ''),
      };
    });
  }, [pressSec?.items, pressSec?.press_releases, page?.press_releases, resolveMedia]);

  // 3. BLOG / ARTICLES DATA
  const articlesSec = liveSections?.['news-articles'] || page?.sections?.['news-articles'] || {};
  const articlesKicker = articlesSec?.kicker || page?.blog_articles_kicker || '';
  const articlesTitle = articlesSec?.title || page?.blog_articles_title || '';
  const articlesDescription = articlesSec?.description || page?.blog_articles_description || '';

  const blogArticles = useMemo(() => {
    const rawList = articlesSec?.items || articlesSec?.blog_articles || articlesSec?.articles || page?.blog_articles || [];
    if (!Array.isArray(rawList)) return [];

    return rawList.map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `article-${index + 1}`,
          title: item,
          category: 'Article',
          tag: 'Article',
          author: '',
          description: '',
          content: '',
          image: '',
          date: '',
          link: '',
        };
      }

      const title = item?.title || '';
      const normalizedId = String(item?.id || title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const resolvedImage = resolveMedia(item?.image);
      const category = item?.category || item?.tag || 'Article';

      return {
        id: normalizedId || `article-${index + 1}`,
        title,
        subtitle: item?.subtitle || '',
        category,
        tag: item?.tag || category,
        author: item?.author || '',
        description: item?.description || '',
        content: item?.content || item?.body || item?.description || '',
        image: resolvedImage,
        date: item?.date || '',
        link: item?.link || (normalizedId ? `news-blog-${normalizedId}` : ''),
      };
    });
  }, [articlesSec?.items, articlesSec?.blog_articles, articlesSec?.articles, page?.blog_articles, resolveMedia]);

  // Available categories for filtering
  const availableCategories = useMemo(() => {
    const cats = new Set();
    blogArticles.forEach((a) => {
      if (a.category) cats.add(a.category);
    });
    return Array.from(cats);
  }, [blogArticles]);

  // Filtered articles based on selected tab
  const filteredArticles = useMemo(() => {
    if (!selectedCategory || selectedCategory === 'ALL') return blogArticles;
    const target = selectedCategory.toLowerCase().trim();
    return blogArticles.filter((a) => {
      const cat = (a.category || a.tag || '').toLowerCase().trim();
      return cat === target || cat.includes(target) || target.includes(cat);
    });
  }, [blogArticles, selectedCategory]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.news-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [selectedCategory, blogArticles, pressReleases]);

  // Ensure newly rendered cards on tab change are immediately visible
  useEffect(() => {
    const cards = document.querySelectorAll('.news-blog-card, .news-blog-feature');
    cards.forEach((card) => card.classList.add('is-visible'));
  }, [selectedCategory, filteredArticles]);

  if (loading && !liveSections) {
    return (
      <section className="news-updates-page" style={{ padding: '60px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div
          style={{
            height: '380px',
            borderRadius: '24px',
            background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
            backgroundSize: '200% 100%',
            animation: 'pulse 1.5s infinite',
            marginBottom: '48px',
          }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px', marginBottom: '48px' }}>
          <div style={{ height: '240px', borderRadius: '16px', background: '#e2e8f0', animation: 'pulse 1.5s infinite' }} />
          <div style={{ height: '240px', borderRadius: '16px', background: '#e2e8f0', animation: 'pulse 1.5s infinite' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
          <div style={{ height: '340px', borderRadius: '16px', background: '#e2e8f0', animation: 'pulse 1.5s infinite' }} />
          <div style={{ height: '340px', borderRadius: '16px', background: '#e2e8f0', animation: 'pulse 1.5s infinite' }} />
          <div style={{ height: '340px', borderRadius: '16px', background: '#e2e8f0', animation: 'pulse 1.5s infinite' }} />
        </div>
      </section>
    );
  }

  return (
    <section className="news-updates-page">
      {/* Press Release Modal (Royal Purple & Gold) */}
      {activePressModal && (
        <div
          className="news-press-modal-overlay"
          onClick={() => setActivePressModal(null)}
        >
          <div
            className="news-press-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Image Header */}
            {activePressModal.image ? (
              <div className="news-press-modal-media">
                <img
                  src={activePressModal.image}
                  alt={activePressModal.title}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div className="news-press-modal-media-overlay" />
                <button
                  type="button"
                  className="news-press-modal-close"
                  onClick={() => setActivePressModal(null)}
                  aria-label="Close press release modal"
                >
                  <X size={18} />
                </button>
                <div className="news-press-modal-header-content">
                  <span className="news-press-modal-category-badge">
                    <Megaphone size={12} /> {activePressModal.category || 'Press Release'}
                  </span>
                  <h3 className="news-press-modal-title">
                    {activePressModal.title}
                  </h3>
                  {activePressModal.subtitle && (
                    <p className="news-press-modal-subtitle">
                      {activePressModal.subtitle}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="news-press-modal-header-plain">
                <button
                  type="button"
                  className="news-press-modal-close plain"
                  onClick={() => setActivePressModal(null)}
                  aria-label="Close press release modal"
                >
                  <X size={18} />
                </button>
                <span className="news-press-modal-category-badge">
                  <Megaphone size={12} /> {activePressModal.category || 'Press Release'}
                </span>
                <h3 className="news-press-modal-title plain">
                  {activePressModal.title}
                </h3>
                {activePressModal.subtitle && (
                  <p className="news-press-modal-subtitle plain">
                    {activePressModal.subtitle}
                  </p>
                )}
              </div>
            )}

            {/* Modal Body */}
            <div className="news-press-modal-body">
              <div className="news-press-modal-meta">
                {activePressModal.date && (
                  <span className="news-press-modal-meta-item">
                    <Calendar size={14} />
                    <span>{activePressModal.date}</span>
                  </span>
                )}
                {activePressModal.date && activePressModal.author && (
                  <span className="news-press-modal-meta-dot">•</span>
                )}
                {activePressModal.author && (
                  <span className="news-press-modal-meta-item">
                    <User size={14} />
                    <span>{activePressModal.author}</span>
                  </span>
                )}
              </div>

              {activePressModal.description && (
                <div className="news-press-modal-quote">
                  <p>"{activePressModal.description}"</p>
                </div>
              )}

              {activePressModal.content && (
                <div className="news-press-modal-content" style={{ whiteSpace: 'pre-line' }}>
                  {activePressModal.content}
                </div>
              )}

              {/* Highlights tags if any */}
              {Array.isArray(activePressModal.highlights) && activePressModal.highlights.length > 0 && (
                <div className="news-press-modal-highlights">
                  <div className="news-press-modal-block-title">Key Highlights</div>
                  <ul>
                    {activePressModal.highlights.map((h, hIdx) => (
                      <li key={hIdx}>{h}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Structured Key Pillars / Features if any */}
              {Array.isArray(activePressModal.features) && activePressModal.features.length > 0 && (
                <div className="news-press-modal-features">
                  <div className="news-press-modal-block-title">Key Pillars & Initiatives</div>
                  <div className="news-press-modal-features-grid">
                    {activePressModal.features.map((feat, fIdx) => (
                      <div key={fIdx} className="news-press-modal-feature-card">
                        <div className="news-press-modal-feature-head">
                          <i className={`fas ${feat.icon || 'fa-handshake'}`} />
                          <span>{feat.title}</span>
                        </div>
                        {feat.detail && (
                          <p className="news-press-modal-feature-detail">
                            {feat.detail}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="news-press-modal-footer">
                <button
                  type="button"
                  className="news-press-modal-btn-cancel"
                  onClick={() => setActivePressModal(null)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="news-press-modal-btn-action"
                  onClick={() => {
                    const id = activePressModal.id;
                    setActivePressModal(null);
                    onNavigate && onNavigate(`news-press-${id}`);
                  }}
                >
                  <span>Full Page View</span>
                  <ExternalLink size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 1. HERO SECTION (Identical to About Us Hero) */}
      <section className="news-hero" id="news-overview">
        <div className="news-hero-media" aria-hidden="true">
          {heroSlides.length > 0 ? (
            heroSlides.map((slide, index) => {
              const isCurrent = index === activeSlide;
              return (
                <div
                  key={slide.id || index}
                  className={`news-hero-slide ${isCurrent ? 'active' : ''}`}
                >
                  {slide.isVideo ? (
                    slide.isYouTube ? (
                      <iframe
                        className="news-hero-video-frame"
                        src={`https://www.youtube.com/embed/${slide.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${slide.youtubeId}&controls=0&playsinline=1&modestbranding=1&rel=0`}
                        title="News Hero video background"
                        allow="autoplay; encrypted-media"
                        tabIndex="-1"
                      />
                    ) : (
                      <video
                        className="news-hero-video-element"
                        src={slide.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      />
                    )
                  ) : (
                    <img
                      src={slide.url}
                      alt={heroTitle || 'News & Updates'}
                      className="news-hero-img-element"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="news-hero-slide active news-hero-gradient-fallback" />
          )}
        </div>

        <div className="news-hero-overlay" aria-hidden="true" />

        <div className="news-hero-content-wrap">
          <div className="news-hero-content">
            {heroKicker && (
              <p className="news-hero-kicker">
                {heroKicker}
              </p>
            )}

            {heroTitle && (
              <h1 className="news-hero-title">
                {heroTitle}
              </h1>
            )}

            {heroSummary && (
              <p className="news-hero-summary" style={{ whiteSpace: 'pre-line' }}>
                {heroSummary}
              </p>
            )}

            {heroSlides.length > 1 && (
              <div className="news-hero-dots" role="tablist" aria-label="News hero carousel navigation">
                {heroSlides.map((slide, index) => (
                  <button
                    key={slide.id || index}
                    type="button"
                    className={`news-hero-dot ${index === activeSlide ? 'active' : ''}`}
                    onClick={() => setActiveSlide(index)}
                    aria-label={`Show news slide ${index + 1}`}
                    role="tab"
                    aria-selected={index === activeSlide}
                  >
                    <span className="news-hero-dot-bar" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 2. PRESS RELEASES SECTION (Royal Purple & Gold Editorial) */}
      {(pressReleases.length > 0 || pressTitle) && (
        <section className="news-press" id="press-releases">
          <div className="news-press-container">
            <div className="news-press-header">
              {pressKicker && (
                <div className="news-press-kicker-badge">
                  <span className="news-press-kicker-dot" />
                  <span>{pressKicker}</span>
                </div>
              )}
              {pressTitle && <h3 className="news-press-title">{pressTitle}</h3>}
              {pressDescription && <p className="news-press-description">{pressDescription}</p>}
            </div>

            {pressReleases.length > 0 && (
              <div className="news-press-grid">
                {pressReleases.map((item, index) => (
                  <article
                    key={item.id}
                    id={item.id}
                    className="news-press-card news-animate"
                    onClick={() => setActivePressModal(item)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setActivePressModal(item);
                      }
                    }}
                  >
                    <div className="news-press-card-accent-bar" />
                    <div className="news-press-card-media">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="news-press-card-media-fallback" />
                      )}
                      <div className="news-press-card-overlay" />
                      <div className="news-press-card-meta-tags">
                        <span className="news-press-tag-category">
                          <Megaphone size={12} />
                          <span>{item.category || 'Press Release'}</span>
                        </span>
                        <span className="news-press-tag-index">
                          {index + 1 < 10 ? `PR · 0${index + 1}` : `PR · ${index + 1}`}
                        </span>
                      </div>
                    </div>

                    <div className="news-press-card-body">
                      <div className="news-press-card-meta-row">
                        {item.date && (
                          <span className="news-press-card-date">
                            <Calendar size={13} />
                            <span>{item.date}</span>
                          </span>
                        )}
                        {item.author && (
                          <span className="news-press-card-author">
                            <User size={13} />
                            <span>{item.author}</span>
                          </span>
                        )}
                      </div>

                      {item.title && <h4 className="news-press-card-title">{item.title}</h4>}
                      {item.description && <p className="news-press-card-desc" style={{ whiteSpace: 'pre-line' }}>{item.description}</p>}

                      <div className="news-press-card-footer">
                        <button
                          type="button"
                          className="news-press-card-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActivePressModal(item);
                          }}
                        >
                          <span>Read Official Release</span>
                          <ArrowRight size={15} />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* 3. BLOG / ARTICLES SECTION (Luxury Royal Purple & Gold) */}
      {(blogArticles.length > 0 || articlesTitle) && (
        <section className="news-blog" id="blog-articles">
          <div className="news-blog-container">
            <div className="news-blog-header">
              {articlesKicker && (
                <div className="news-blog-kicker-badge">
                  <span className="news-blog-kicker-dot" />
                  <span>{articlesKicker}</span>
                </div>
              )}
              {articlesTitle && <h3 className="news-blog-title">{articlesTitle}</h3>}
              {articlesDescription && <p className="news-blog-description">{articlesDescription}</p>}
            </div>

            {/* Category Filter Pills */}
            {availableCategories.length > 1 && (
              <div className="news-blog-filter-bar">
                <button
                  type="button"
                  onClick={() => setSelectedCategory('ALL')}
                  className={`news-blog-filter-btn ${selectedCategory === 'ALL' ? 'active' : ''}`}
                >
                  All Articles ({blogArticles.length})
                </button>
                {availableCategories.map((cat) => {
                  const count = blogArticles.filter((a) => (a.category || a.tag) === cat).length;
                  const isActive = selectedCategory === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setSelectedCategory(cat)}
                      className={`news-blog-filter-btn ${isActive ? 'active' : ''}`}
                    >
                      {cat} {count > 0 ? `(${count})` : ''}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Articles Cards Grid */}
            {filteredArticles.length > 0 && (
              <div className="news-blog-grid">
                {filteredArticles.map((item) => (
                  <article
                    key={item.id}
                    id={item.id}
                    className="news-blog-card news-animate is-visible"
                    onClick={() => onNavigate && onNavigate(item.link || `news-blog-${item.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        onNavigate && onNavigate(item.link || `news-blog-${item.id}`);
                      }
                    }}
                  >
                    <div className="news-blog-card-accent-bar" />
                    <div className="news-blog-card-media">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="news-blog-card-media-fallback" />
                      )}
                      <div className="news-blog-card-overlay" />
                      <div className="news-blog-card-meta-tags">
                        <span className="news-blog-tag-category">
                          <BookOpen size={12} />
                          <span>{item.category || item.tag || 'Article'}</span>
                        </span>
                        {item.date && (
                          <span className="news-blog-tag-date">
                            <Calendar size={11} />
                            <span>{item.date}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="news-blog-card-body">
                      {item.author && (
                        <div className="news-blog-card-author-row">
                          <User size={13} />
                          <span>{item.author}</span>
                        </div>
                      )}
                      {item.title && <h4 className="news-blog-card-title">{item.title}</h4>}
                      {item.description && <p className="news-blog-card-desc" style={{ whiteSpace: 'pre-line' }}>{item.description}</p>}

                      <div className="news-blog-card-footer">
                        <span className="news-blog-card-link">
                          <span>Read Full Story</span>
                          <ArrowRight size={15} />
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </section>
  );
}

export default NewsUpdatesPage;
